import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const PHONE_ROUTE_MAX_WIDTH = 900;
const TABLET_ROUTE_MAX_WIDTH = 1400;
const ROUTE_MODE_KEY = "vb-mobile-route-mode";

const isMobileViewport = () => {
  if (typeof window === "undefined") return false;
  const isPhoneWidth =
    window.matchMedia?.(`(max-width: ${PHONE_ROUTE_MAX_WIDTH}px)`)?.matches ??
    window.innerWidth <= PHONE_ROUTE_MAX_WIDTH;
  const isTabletTouchWidth =
    (window.matchMedia?.("(pointer: coarse)")?.matches || window.navigator?.maxTouchPoints > 1) &&
    (window.matchMedia?.(`(max-width: ${TABLET_ROUTE_MAX_WIDTH}px)`)?.matches ??
      window.innerWidth <= TABLET_ROUTE_MAX_WIDTH);

  return (
    isPhoneWidth ||
    isTabletTouchWidth
  );
};

export const mapToMobilePath = (pathname) => {
  if (!pathname) return "/m";
  if (pathname === "/") return "/m";
  if (pathname.startsWith("/m/") || pathname === "/m") return pathname;

  // Public pages
  if (pathname === "/catalog" || pathname.startsWith("/catalog/")) return `/m${pathname}`;
  if (pathname.startsWith("/valve-bodies/")) return `/m${pathname}`;
  if (pathname === "/qr" || pathname === "/download" || pathname === "/scan") return `/m${pathname}`;
  if (pathname === "/register" || pathname === "/distributor/apply" || pathname === "/partners/apply") return `/m${pathname}`;
  if (pathname.startsWith("/register/valve-body/")) return pathname;
  if (pathname === "/authentication/sign-in") return "/m/authentication/sign-in";
  if (pathname === "/account" || pathname.startsWith("/account/")) return `/m${pathname}`;
  if (pathname === "/post-login") return "/m/post-login";
  // Explicitly support gallery deep-linking on phones
  if (pathname === "/media/gallery") return "/m/media/gallery";

  // Staff mobile pages. Keep Build Sheets intentionally blocked by MobileRoot.
  const staffPrefixes = [
    "/dashboard",
    "/auth-dashboard",
    "/search",
    "/pipeline",
    "/work-orders",
    "/build-sheets",
    "/inventory",
    "/sales",
    "/pull-orders",
    "/core-returns",
    "/invoices",
    "/payments",
    "/purchase-orders",
    "/warehouse",
    "/customers",
    "/vendors",
    "/projections",
    "/settings",
  ];
  if (staffPrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return `/m${pathname}`;
  }

  // Default: don't force unknown routes into /m.
  return null;
};

/**
 * True when the current browser should use the /m/* route space
 * (?mobile=1 / ?desktop=1 preference, else phone/tablet viewport).
 * Shared with components/LegacyStaffRedirect so legacy desktop ERP URLs opened
 * on a phone still go to the mobile app instead of hard-navigating to /staff.
 */
export const shouldUseMobileRouteSpace = (search = "") => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(search || window.location.search || "");
  let storedMode = null;
  try {
    storedMode = window.localStorage?.getItem(ROUTE_MODE_KEY);
  } catch {
    storedMode = null;
  }
  const routeMode =
    params.get("mobile") === "1" ? "mobile" : params.get("desktop") === "1" ? "desktop" : storedMode;
  return routeMode === "mobile" || (routeMode !== "desktop" && isMobileViewport());
};

const mapToDesktopPath = (pathname) => {
  if (!pathname) return "/";
  if (pathname === "/m" || pathname === "/m/") return "/";
  if (!pathname.startsWith("/m/")) return pathname;

  // Mobile-only routes
  if (pathname === "/m/dashboard-home") return "/dashboard";

  // Default: strip /m prefix
  return pathname.slice(2);
};

/**
 * Auto-redirect phone/tablet-sized viewports to the mobile route space.
 *
 * Strict behavior:
 * - Phone/tablet-sized viewports always use /m/* (including sign-in).
 * - Desktop-sized viewports use desktop routes unless ?mobile=1 is selected.
 * - ?desktop=1 and ?mobile=1 persist the user's route-mode preference.
 */
export default function MobileAutoRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  const mobileTarget = useMemo(() => mapToMobilePath(location.pathname), [location.pathname]);
  const desktopTarget = useMemo(() => mapToDesktopPath(location.pathname), [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith("/storefront-preview")) return;

    const params = new URLSearchParams(location.search);
    const requestedMode = params.get("mobile") === "1" ? "mobile" : params.get("desktop") === "1" ? "desktop" : null;
    if (requestedMode && typeof window !== "undefined") {
      window.localStorage?.setItem(ROUTE_MODE_KEY, requestedMode);
    }

    const storedMode = typeof window !== "undefined" ? window.localStorage?.getItem(ROUTE_MODE_KEY) : null;
    const routeMode = requestedMode || storedMode;
    const isMobile = routeMode === "mobile" || (routeMode !== "desktop" && isMobileViewport());

    // Mobile viewport: enforce /m mapping where supported.
    if (isMobile) {
      if (!mobileTarget) return;
      if (location.pathname === mobileTarget) return;
      navigate(mobileTarget + location.search, { replace: true });
      return;
    }

    // Desktop viewport: keep users out of /m/*.
    if (location.pathname.startsWith("/m")) {
      if (desktopTarget === location.pathname) return;
      navigate(desktopTarget + location.search, { replace: true });
    }
  }, [mobileTarget, desktopTarget, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let frame = null;
    const reevaluateRouteSpace = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const params = new URLSearchParams(window.location.search);
        const storedMode = window.localStorage?.getItem(ROUTE_MODE_KEY);
        const routeMode = params.get("mobile") === "1" ? "mobile" : params.get("desktop") === "1" ? "desktop" : storedMode;
        const shouldUseMobile = routeMode === "mobile" || (routeMode !== "desktop" && isMobileViewport());
        const currentPath = window.location.pathname;
        const target = shouldUseMobile ? mapToMobilePath(currentPath) : mapToDesktopPath(currentPath);

        if (!target || target === currentPath) return;
        navigate(target + window.location.search, { replace: true });
      });
    };

    window.addEventListener("resize", reevaluateRouteSpace);
    window.addEventListener("orientationchange", reevaluateRouteSpace);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", reevaluateRouteSpace);
      window.removeEventListener("orientationchange", reevaluateRouteSpace);
    };
  }, [navigate]);

  return null;
}
